/**
 * tilePrewarm — ADR-037 Phase 1, pre-warm B (client-side background tile warm).
 *
 * Problem (founder symptom «клик и жди»): NDVI raster tiles are rendered
 * on-demand only when the user opens /map, picks a scope+date and MapLibre
 * fires the first tile request. A cold COG render is ~700ms/tile, so the first
 * paint after «Карта» is a stutter even though we already know — the moment the
 * user logs in — which farm + which latest date they will almost certainly view
 * first.
 *
 * Pre-warm B moves that warming **earlier and into the background**: right after
 * login (AppShell /auth/me success), on a NON-map screen, we kick off a tiny,
 * bounded, low-priority set of **detached** tile fetches (`new Image()`) for the
 * user's first farm × latest NDVI date × the farm-view zoom. By the time they
 * click «Карта», those exact tiles are already in the browser + nginx edge
 * cache (ADR-037 nginx edge-cache lot, #338), so MapLibre's first request is a
 * cache HIT.
 *
 * ## Why detached `new Image()` and NOT a MapLibre source
 *
 * This is the load-bearing safety constraint. We NEVER touch a live MapLibre
 * source here — no `addSource`/`setTiles`/`removeSource`. Two reasons:
 *   1. There is no map instance on a non-map screen — there's nothing to touch.
 *   2. Even on /map, churning a live source is exactly the AK-119 regression
 *      class (stale tiles leaking across scopes, source-recreation flashes).
 *
 * `new Image().src = <tile url>` issues a plain same-origin GET that:
 *   - shares the browser HTTP cache + nginx edge cache with MapLibre's own
 *     raster fetches (same URL → same cache key → MapLibre gets a HIT), and
 *   - sends the auth cookie automatically (same-origin, no `crossOrigin`), so
 *     the protected /api/v1/ndvi/* routes authorise exactly as the map would.
 *
 * The Image objects are intentionally detached (never added to the DOM) and we
 * drop our reference once they settle — they exist only to populate the cache.
 *
 * ## Cache-key exactness
 *
 * We reuse `buildRasterUrl` from rasterSourceEffect.ts so the path + query
 * (`?farm_id=…`) is byte-identical to what the live source builds. We then
 * expand the `{z}/{x}/{y}` template into concrete tile coords. MapLibre resolves
 * its relative tile URLs against `window.location.origin`, so we warm the same
 * absolute origin (credentials stripped — mirrors MapCanvas `transformRequest`).
 *
 * ## Bounded & polite (see warmFirstView)
 *   - One product only: NDVI (the default active overlay).
 *   - One scope: the user's first farm (the first marker in /map-init).
 *   - One date: the latest available NDVI date for that farm.
 *   - One zoom: the farm-view zoom (what fitBounds lands on, capped).
 *   - Tile cap: PREWARM_TILE_CAP — if the bbox needs more tiles than the cap we
 *     skip entirely rather than warm a partial/lopsided set (a too-zoomed-out
 *     farm would otherwise blow the budget). Default cap ≈ a single farm
 *     viewport's worth of tiles.
 *   - Skipped on Save-Data / no farms / already warmed (dedupe).
 *   - Fire-and-forget; failures are swallowed (warming is best-effort).
 *
 * Spec: ADR-037 (docs/superpowers/adr/2026-06-25-map-perf-3x.md), Phase 1.
 * Complementary to pre-warm A (server-side render-at-scene_complete,
 * domain-satellite) — A makes the COG warm on the server; B makes the HTTP
 * response warm in the edge/browser cache. Both reduce first-paint latency.
 */
import { buildRasterUrl } from './effects/rasterSourceEffect';
import type { BBox } from './bbox';

/**
 * Max tiles we will ever warm in one pre-warm pass. A typical farm at the
 * farm-view zoom fits in well under this; if a farm's bbox would need more
 * (very large / very low zoom), we skip the pass entirely (warmFirstView) so we
 * never hammer the backend with a wide cold-storm of renders. Keep this small
 * and polite — the goal is "first view feels instant", not "preload the world".
 */
export const PREWARM_TILE_CAP = 12;

/**
 * The integer zooms we warm. These MUST match the zoom MapLibre actually
 * requests tiles at after a farm fitBounds, because a tile URL is keyed by
 * (z,x,y) — a z13 warmed tile is a DIFFERENT cache key from a z15 request, and
 * nginx will NOT serve one level for another. So if we guess the wrong zoom,
 * the warm is a pure miss.
 *
 * MapCanvas farm fitBounds uses `maxZoom: 15, padding: 40` (MapCanvas L311–320).
 * The landed integer zoom therefore depends on farm size:
 *   - large farm → fit constrains below 15 (empirically ~13),
 *   - small/medium farm → hits the maxZoom cap → lands at 15.
 * We can't know the farm's apparent size cheaply here, so we warm BOTH candidate
 * levels (13 and the fit cap 15) and split the tile budget across them. This
 * costs at most PREWARM_TILE_CAP tiles total (still bounded) and guarantees the
 * level MapLibre lands on is among the warmed ones for both large and small
 * farms. Bench (ADR-037) should confirm real hit-rate and prune to one level if
 * the data says so.
 *
 * Primary first (13) — for large farms it's the landed level and we want it
 * warm first; the cap-15 set follows.
 */
export const PREWARM_ZOOMS = [13, 15] as const;

/** @deprecated kept for any external import; primary warm zoom. */
export const PREWARM_ZOOM = PREWARM_ZOOMS[0];

interface TileCoord {
  z: number;
  x: number;
  y: number;
}

/**
 * Web-Mercator slippy-map tile X/Y for a lng/lat at integer zoom z.
 * Standard OSM formula. Pure — exported for unit tests.
 */
export function lngLatToTile(lng: number, lat: number, z: number): { x: number; y: number } {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  );
  // Clamp into valid tile range [0, n-1] — guards against poles / antimeridian.
  const clamp = (v: number) => Math.min(n - 1, Math.max(0, v));
  return { x: clamp(x), y: clamp(y) };
}

/**
 * Enumerate the tile coords covering a bbox at integer zoom z, **center-out**,
 * truncated to at most `capCount` tiles.
 *
 * bbox is [minLng, minLat, maxLng, maxLat] (the shape /map-init markers use).
 *
 * Why center-out + truncate (not skip-on-overflow): the farm fitBounds centers
 * the farm in the viewport, so the bbox-center tiles are exactly the ones
 * MapLibre paints first and the user looks at first. A large farm at a high
 * zoom (e.g. z15) can cover dozens of tiles — far more than our polite cap — so
 * instead of skipping that level entirely (the old behaviour, which left small
 * farms' z15 level — the very level they land on — completely cold) we warm the
 * `capCount` tiles closest to the center. This is bounded (≤ capCount, the cap
 * is what protects the backend) and high-value (center first), and it is NOT a
 * lopsided corner — center-out fills symmetrically.
 *
 * Returns [] only for a degenerate/empty cover or capCount ≤ 0.
 *
 * Pure — exported for unit tests.
 */
export function tilesForBBox(bbox: BBox, z: number, capCount: number): TileCoord[] {
  if (capCount <= 0) return [];
  const [minLng, minLat, maxLng, maxLat] = bbox;
  // Note: tile Y grows southward, so maxLat → smaller y. Sort the pair.
  const a = lngLatToTile(minLng, maxLat, z); // top-left
  const b = lngLatToTile(maxLng, minLat, z); // bottom-right
  const x0 = Math.min(a.x, b.x);
  const x1 = Math.max(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const y1 = Math.max(a.y, b.y);
  if (x1 < x0 || y1 < y0) return [];

  // Enumerate the full cover, then sort by Chebyshev distance from the bbox
  // center tile so truncation keeps the center neighbourhood.
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const all: TileCoord[] = [];
  for (let x = x0; x <= x1; x++) {
    for (let y = y0; y <= y1; y++) {
      all.push({ z, x, y });
    }
  }
  all.sort((p, q) => {
    const dp = Math.max(Math.abs(p.x - cx), Math.abs(p.y - cy));
    const dq = Math.max(Math.abs(q.x - cx), Math.abs(q.y - cy));
    return dp - dq;
  });
  return all.slice(0, capCount);
}

/**
 * Expand a tile-template URL ("/api/v1/ndvi/raster/{date}/ndvi/{z}/{x}/{y}.png")
 * into a concrete tile URL for the given coords.
 *
 * Pure — exported for unit tests.
 */
export function expandTileTemplate(template: string, tile: TileCoord): string {
  return template
    .replace('{z}', String(tile.z))
    .replace('{x}', String(tile.x))
    .replace('{y}', String(tile.y));
}

/**
 * True when we should NOT warm: the user opted into data-saving / is on a
 * metered or slow connection. `navigator.connection` is non-standard (Chromium
 * only) so we feature-detect and treat absence as "ok to warm".
 */
export function shouldSkipForConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  // 2g / slow-2g → warming would compete with real navigation; skip.
  if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return true;
  return false;
}

// Module-level dedupe — warm each absolute tile URL at most once per session.
// Prevents a re-render / second login-effect from re-issuing the same GETs.
const warmed = new Set<string>();

/**
 * Resolve a relative tile path to the same absolute URL MapLibre would request:
 * same origin as the page, credentials stripped (mirrors MapCanvas
 * `transformRequest`). Returns null if it can't be parsed (we just skip it).
 */
function toAbsoluteSameOrigin(path: string): string | null {
  try {
    const u = new URL(path, window.location.origin);
    u.username = '';
    u.password = '';
    return u.toString();
  } catch {
    return null;
  }
}

/**
 * Fire detached, low-priority GETs for a list of absolute tile URLs via
 * `new Image()`. Fire-and-forget: we attach noop handlers only to release the
 * reference and never throw. Dedupes against the module-level `warmed` set.
 *
 * `fetchPriority='low'` is a hint (Chromium) so these never contend with the
 * real navigation / app data fetches.
 */
export function warmTiles(urls: string[]): void {
  for (const url of urls) {
    if (warmed.has(url)) continue;
    warmed.add(url);
    try {
      const img = new Image();
      // Low-priority hint — supported in Chromium; ignored elsewhere.
      (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'low';
      // decoding=async + no DOM attach → pure cache populate.
      img.decoding = 'async';
      const done = () => {
        img.onload = null;
        img.onerror = null;
      };
      img.onload = done;
      img.onerror = done;
      img.src = url;
    } catch {
      /* best-effort — never let warming break the app */
    }
  }
}

/** Test-only: reset the per-session dedupe set. */
export function __resetWarmedForTest(): void {
  warmed.clear();
}

export interface FarmLike {
  id: string;
  bbox?: BBox | null;
}

export interface WarmFirstViewArgs {
  /** The user's first farm (first /map-init marker). */
  farm: FarmLike | null | undefined;
  /** Latest available NDVI date for that farm (ISO YYYY-MM-DD), or null. */
  latestDate: string | null | undefined;
  /**
   * Candidate integer zooms to warm. Defaults to PREWARM_ZOOMS (the landed
   * zoom + the fitBounds cap), so we hit MapLibre's actual request level for
   * both large and small farms. Tests pass a single-element array to pin one
   * level.
   */
  zooms?: readonly number[];
  /** Total tile cap across ALL zooms. Defaults to PREWARM_TILE_CAP. */
  cap?: number;
}

/**
 * The bounded, polite orchestrator: warm the NDVI tiles for the user's first
 * farm × latest date × the candidate farm-view zooms. Returns the list of
 * absolute URLs it warmed (mostly for testing / logging) — `[]` when it
 * deliberately skipped.
 *
 * Tile budget: `cap` is the TOTAL across all zooms. We split it evenly per
 * candidate zoom (`floor(cap / nZooms)`, min 1) and warm the **center-out**
 * tiles of the farm bbox at each level (tilesForBBox). A large farm whose z15
 * footprint exceeds its slice gets its center z15 tiles warmed (the ones first
 * painted) rather than that level being dropped — so both the landed-zoom
 * (large farm ~13) and the cap-zoom (small farm 15) levels get useful coverage.
 * The grand total can never exceed `cap`.
 *
 * Skips entirely (returns []) only when:
 *   - Save-Data / slow connection.
 *   - No farm, no farm bbox, or no latest date (nothing to warm).
 *   - No candidate zooms.
 *
 * Caller (useTilePrewarmEffect / AppShell) decides WHEN to run this (idle,
 * post-login); this function decides WHETHER + WHAT, and stays cheap.
 */
export function warmFirstView(args: WarmFirstViewArgs): string[] {
  const { farm, latestDate } = args;
  const zooms = args.zooms ?? PREWARM_ZOOMS;
  const cap = args.cap ?? PREWARM_TILE_CAP;

  if (shouldSkipForConnection()) return [];
  if (!farm || !farm.id || !farm.bbox) return [];
  if (!latestDate) return [];
  if (zooms.length === 0) return [];

  // Same path the live source builds — exact cache-key match. NDVI only.
  const template = buildRasterUrl('ndvi', latestDate, 'farm', farm.id);
  if (!template) return [];

  // Total cap split evenly across candidate zooms (≥1 each). tilesForBBox warms
  // center-out and truncates to this slice, so a high zoom contributes its
  // center neighbourhood rather than blowing the budget.
  const perZoomCap = Math.max(1, Math.floor(cap / zooms.length));

  const urls: string[] = [];
  for (const z of zooms) {
    if (urls.length >= cap) break; // hard total cap guard
    const tiles = tilesForBBox(farm.bbox, z, perZoomCap);
    for (const t of tiles) {
      if (urls.length >= cap) break;
      const abs = toAbsoluteSameOrigin(expandTileTemplate(template, t));
      if (abs) urls.push(abs);
    }
  }
  if (urls.length === 0) return [];
  warmTiles(urls);
  return urls;
}
